/**
 * Validation utilities for Connect Form
 * Validates WhatsApp number, full name, and site location
 */

/**
 * Indian mobile number regex
 * - 10 digits total
 * - First digit must be 4-9 (cannot be 0, 1, 2, or 3)
 */
export const INDIAN_PHONE_REGEX = /^[4-9][0-9]{9}$/;

/**
 * Restricts input to only digits and limits to 10 digits maximum
 * @param {string} value - Input value to restrict
 * @returns {string} Restricted value (only digits, max 10 digits)
 */
export function restrictToTenDigits(value) {
  if (!value) return "";
  // Remove any non-digit characters
  const digitsOnly = value.replace(/\D/g, "");
  // Limit to 10 digits
  return digitsOnly.slice(0, 10);
}

/**
 * Validates Indian WhatsApp/mobile number
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateWhatsAppNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return false;
  }
  // Remove any spaces, dashes, or other formatting
  const cleaned = phoneNumber.trim().replace(/[\s-]/g, "");
  return INDIAN_PHONE_REGEX.test(cleaned);
}

/**
 * Gets error message for WhatsApp number validation
 * @param {string} phoneNumber - Phone number to validate
 * @returns {string|null} Error message or null if valid
 */
export function getWhatsAppNumberError(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== "string") {
    return "Please Enter Your Phone Number";
  }
  const cleaned = phoneNumber.trim().replace(/[\s-]/g, "");
  if (cleaned.length === 0) {
    return "Please Enter Your Phone Number";
  }
  if (cleaned.length < 10) {
    return "Please enter a valid 10-digit mobile number";
  }
  if (!INDIAN_PHONE_REGEX.test(cleaned)) {
    return "Please enter a valid 10-digit mobile number starting with 4-9";
  }
  return null;
}

/**
 * Validates full name
 * @param {string} fullName - Full name to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateFullName(fullName) {
  if (!fullName || typeof fullName !== "string") {
    return false;
  }
  const trimmed = fullName.trim();
  // At least 2 characters and contains at least one letter
  return trimmed.length >= 2 && /[a-zA-Z]/.test(trimmed);
}

/**
 * Gets error message for full name validation
 * @param {string} fullName - Full name to validate
 * @returns {string|null} Error message or null if valid
 */
export function getFullNameError(fullName) {
  if (!fullName || typeof fullName !== "string") {
    return "Please enter your full name";
  }
  const trimmed = fullName.trim();
  if (trimmed.length === 0) {
    return "Please enter your full name";
  }
  if (trimmed.length < 2) {
    return "Name must be at least 2 characters";
  }
  if (!/[a-zA-Z]/.test(trimmed)) {
    return "Name must contain at least one letter";
  }
  return null;
}

/**
 * Validates site location
 * @param {string} siteLocation - Site location to validate
 * @returns {boolean} True if valid, false otherwise
 */
export function validateSiteLocation(siteLocation) {
  if (!siteLocation || typeof siteLocation !== "string") {
    return false;
  }
  const trimmed = siteLocation.trim();
  // At least 2 characters
  return trimmed.length >= 2;
}

/**
 * Gets error message for site location validation
 * Common error message that works for both selection mode and manual entry mode
 * @param {string} siteLocation - Site location to validate
 * @returns {string|null} Error message or null if valid
 */
export function getSiteLocationError(siteLocation) {
  if (!siteLocation || typeof siteLocation !== "string") {
    return "Please provide a site location";
  }
  const trimmed = siteLocation.trim();
  if (trimmed.length === 0) {
    return "Please provide a site location";
  }
  if (trimmed.length < 2) {
    return "Location must be at least 2 characters";
  }
  return null;
}

/**
 * Validates all form fields
 * @param {Object} formData - Form data object
 * @param {string} formData.whatsappNumber - WhatsApp number
 * @param {string} formData.fullName - Full name
 * @param {string} formData.siteLocation - Site location
 * @returns {Object} Validation errors object with field names as keys
 */
export function validateConnectForm({
  whatsappNumber,
  fullName,
  siteLocation,
}) {
  const errors = {};

  if (!validateWhatsAppNumber(whatsappNumber)) {
    errors.whatsappNumber = "Please enter a valid 10-digit mobile number";
  }

  if (!validateFullName(fullName)) {
    errors.fullName = "Please enter a valid full name (at least 2 characters)";
  }

  if (!validateSiteLocation(siteLocation)) {
    errors.siteLocation = "Please select a valid site location";
  }

  return errors;
}
